import { getGetClientsUrl } from 'generatedTypes/client/services/client-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { ClientResponse, FetchClients, FetchClientsParams } from './types';

const useClients = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchClients: FetchClients = async request => {
        try {
            setIsLoading(true);
            const url = getGetClientsUrl(request);

            return await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchClients(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: clientsData,
        fetch: paginatedFetchClients,
        reset
    } = usePagination<ClientResponse>({
        callback: fetchClients
    });

    const handleFetchClients = (args?: FetchClientsParams) => {
        const { name, pageSize = DEFAULT_PAGE_SIZE, sort } = args || {};

        return paginatedFetchClients({
            name,
            pageSize,
            sort
        });
    };

    return {
        clients: clientsData as ClientResponse[],
        fetch: handleFetchClients,
        isLoading,
        reset
    };
};

export default useClients;
